import React, { useState } from "react";

import { IDog, IDogRun } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import Button from "../../../components/Button/Button";
import Input from "../../../components/Input/Input";

import "./ObedienceRunForm.scss";

interface IProps {
  dog: IDog;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  editRun?: IDogRun;
}

const OBEDIENCE_CLASSES = [
  { value: 'Novice A', label: 'Novice A' },
  { value: 'Novice B', label: 'Novice B' },
  { value: 'Open A', label: 'Open A' },
  { value: 'Open B', label: 'Open B' },
  { value: 'Utility A', label: 'Utility A' },
  { value: 'Utility B', label: 'Utility B' }
];

const RESULT_STATUSES = [
  { value: Constants.run_result_status.qualified, label: 'Q (Qualified)' },
  { value: Constants.run_result_status.not_qualified, label: 'NQ (Not Qualified)' },
  { value: Constants.run_result_status.absent, label: 'ABS (Absent)' }
];

const ObedienceRunForm: React.FC<IProps> = ({ dog, onSubmit, onCancel, isSubmitting, editRun }) => {
  // Form fields
  const [trialDate, setTrialDate] = useState<string>(
    editRun?.trial_date ? new Date(editRun.trial_date).toISOString().split('T')[0] : ''
  );
  const [obedienceClass, setObedienceClass] = useState<string>(editRun?.class || '');
  const [score, setScore] = useState<string>(editRun?.score?.toString() || '');
  const [resultStatus, setResultStatus] = useState<string>(editRun?.result_status || '');
  const [judgeNumber, setJudgeNumber] = useState<string>(editRun?.judge_number || '');
  const [judgeName, setJudgeName] = useState<string>(editRun?.judge_name || '');
  const [eventName, setEventName] = useState<string>(editRun?.event_name || '');
  const [city, setCity] = useState<string>(editRun?.location?.city || '');
  const [state, setState] = useState<string>(editRun?.location?.state || '');
  const [notes, setNotes] = useState<string>(editRun?.notes || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Parse the date and set it to noon local time to avoid timezone issues
    const [year, month, day] = trialDate.split('-').map(Number);
    const localDate = new Date(year, month - 1, day, 12, 0, 0);
    
    const data: any = {
      dogId: dog._id,
      obedienceClass,
      score: parseFloat(score),
      resultStatus,
      trialDate: localDate,
      judgeNumber: judgeNumber || undefined,
      judgeName: judgeName || undefined,
      eventName: eventName || undefined,
      location: (city || state) ? { city, state } : undefined,
      notes: notes || undefined
    };

    if (editRun) {
      data.run_id = editRun._id;
    }

    onSubmit(data);
  };

  const isValid = trialDate && obedienceClass && score && resultStatus;

  return (
    <form onSubmit={handleSubmit} className="obedienceRunForm">
      <div className="formGrid">
        {/* Trial Date */}
        <div className="fieldGroup">
          <label htmlFor="trialDate">
            Trial Date <span className="required">*</span>
          </label>
          <input
            type="date"
            id="trialDate"
            value={trialDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTrialDate(e.target.value)}
            required
          />
        </div>

        {/* Class */}
        <div className="fieldGroup">
          <label htmlFor="obedienceClass">
            Class <span className="required">*</span>
          </label>
          <select
            id="obedienceClass"
            value={obedienceClass}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setObedienceClass(e.target.value)}
            required
          >
            <option value="">Select Class</option>
            {OBEDIENCE_CLASSES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* Score */}
        <div className="fieldGroup">
          <label htmlFor="score">
            Score (out of 200) <span className="required">*</span>
          </label>
          <Input
            id="score"
            type="number"
            min="0"
            max="200"
            step="0.5"
            value={score}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setScore(e.target.value)}
            placeholder="0-200 (half points allowed)"
            required
          />
          <span className="fieldHelp">
            Minimum 170 required for qualification. Half points allowed (e.g., 185.5)
          </span>
        </div>

        {/* Result Status */}
        <div className="fieldGroup">
          <label htmlFor="resultStatus">
            Result <span className="required">*</span>
          </label>
          <select
            id="resultStatus"
            value={resultStatus}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setResultStatus(e.target.value)}
            required
          >
            <option value="">Select Result</option>
            {RESULT_STATUSES.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        {/* Judge Number */}
        <div className="fieldGroup">
          <label htmlFor="judgeNumber">Judge Number</label>
          <Input
            id="judgeNumber"
            value={judgeNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJudgeNumber(e.target.value)}
            placeholder="Optional"
          />
          <span className="fieldHelp">
            Preferred for uniqueness (e.g., AKC judge number)
          </span>
        </div>

        {/* Judge Name */}
        <div className="fieldGroup">
          <label htmlFor="judgeName">Judge Name</label>
          <Input
            id="judgeName"
            value={judgeName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJudgeName(e.target.value)}
            placeholder="Optional"
          />
          <span className="fieldHelp">
            Used as fallback if judge number not available
          </span>
        </div>

        {/* Event Name */}
        <div className="fieldGroup">
          <label htmlFor="eventName">Event Name</label>
          <Input
            id="eventName"
            value={eventName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEventName(e.target.value)}
            placeholder="Optional"
          />
        </div>

        {/* City */}
        <div className="fieldGroup">
          <label htmlFor="city">City</label>
          <Input
            id="city"
            value={city}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value)}
            placeholder="Optional"
          />
        </div>

        {/* State */}
        <div className="fieldGroup">
          <label htmlFor="state">State</label>
          <Input
            id="state"
            value={state}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setState(e.target.value)}
            placeholder="Optional"
          />
        </div>

        {/* Notes - full width */}
        <div className="fieldGroup fullWidth">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
            placeholder="Optional notes about this run"
            rows={3}
          />
        </div>
      </div>

      {/* Form Actions */}
      <div className="formActions">
        <Button
          onClick={onCancel}
          variant="secondary"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="default"
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? "Saving..." : editRun ? "Update Run" : "Add Run"}
        </Button>
      </div>
    </form>
  );
};

export default ObedienceRunForm;

